/**
 * TextInput — a single-line text field wrapped with an optional label, hint,
 * and error message. The field shows a rose focus ring and a coral invalid
 * state. Pass `error` (a string) to mark it invalid and show the message.
 */
import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from "react";

export interface TextInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  /** Visible label above the field. */
  readonly label?: ReactNode;
  /** Helper text under the field (hidden when an error is shown). */
  readonly hint?: ReactNode;
  /** Error message — when set, the field renders its invalid state. */
  readonly error?: string;
  /** Marks the field required (adds a rose asterisk to the label). */
  readonly required?: boolean;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  function TextInput(
    { label, hint, error, required, id, className, ...rest },
    ref,
  ) {
    const autoId = useId();
    const inputId = id ?? autoId;
    const hintId = `${inputId}-hint`;
    const errorId = `${inputId}-error`;
    const invalid = Boolean(error);
    const describedBy = invalid ? errorId : hint != null ? hintId : undefined;

    return (
      <div className={["lc-field", className].filter(Boolean).join(" ")}>
        {label != null ? (
          <label className="lc-field__label" htmlFor={inputId}>
            {label}
            {required ? <span className="lc-field__required" aria-hidden="true">*</span> : null}
          </label>
        ) : null}
        <input
          ref={ref}
          id={inputId}
          className="lc-input"
          data-invalid={invalid || undefined}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          aria-required={required || undefined}
          required={required}
          {...rest}
        />
        {invalid ? (
          <span id={errorId} className="lc-field__error" role="alert">
            {error}
          </span>
        ) : hint != null ? (
          <span id={hintId} className="lc-field__hint">
            {hint}
          </span>
        ) : null}
      </div>
    );
  },
);
